import { LitElement, html, css } from 'lit-element';
import { connect } from '../store/connect';
import { actions, selectors } from './';
import { selectors as accountSelectors } from '../account';
import { DETAIL_MODES } from '../form/constants';
import '../routing/routed-button';
import './generate-token-btn';
import './reset-password-btn';
import './act-as-btn';
import './user-devices';
import './user-history';
import { UserDetailBase } from './user-detail-base';
import { AUTHORIZATIONS } from '../authorizations/authorizations';
const { CAN_EDIT_USER_TOKENS, CAN_SEE_USER_DEVICES, CAN_SEE_USER_HISTORY } = AUTHORIZATIONS;
class UserDetail extends UserDetailBase {
    constructor() {
        super();
        this.renderTemplateItem = this.renderTemplateItem.bind(this);
        this.detailMode = DETAIL_MODES.VIEW;
    }

    static get styles() {
        return [
            ...super.styles,
            css`
                tmn-tab-group {
                    margin-top: 6px;
                    margin-left: 6px;
                }
            `,
        ];
    }

    static get properties() {
        return {};
    }

    renderMain() {
        const { authorizations } = this;
        if (
            [CAN_SEE_USER_DEVICES, CAN_SEE_USER_HISTORY].some((auth) =>
                authorizations.includes(auth)
            ) &&
            !this.isAddNewMode()
        ) {
            return this.renderAdvancedAdminView();
        }
        return this.renderUserDetails();
    }

    renderUserDetails() {
        return html`${this.renderControls()} ${this.renderValidationErrors()} ${this.renderForm()}`;
    }

    renderAdvancedAdminView() {
        return html`
            <tmn-tab-group>
                <tmn-tabs>
                    <tmn-tab>User</tmn-tab>
                    ${this.authorizations.includes(CAN_SEE_USER_DEVICES)
                        ? html`<tmn-tab>Devices</tmn-tab>`
                        : ''}
                    ${this.authorizations.includes(CAN_SEE_USER_HISTORY)
                        ? html`<tmn-tab>History</tmn-tab>`
                        : ''}
                </tmn-tabs>
                <tmn-tab-panel>${this.renderUserDetails()}</tmn-tab-panel>
                ${this.authorizations.includes(CAN_SEE_USER_DEVICES)
                    ? html`<tmn-tab-panel>${this.renderDevices()}</tmn-tab-panel>`
                    : ''}
                ${this.authorizations.includes(CAN_SEE_USER_HISTORY)
                    ? html`<tmn-tab-panel>${this.renderHistory()}</tmn-tab-panel>`
                    : ''}
            </tmn-tab-group>
        `;
    }

    renderDevices() {
        return html`<user-devices></user-devices>`;
    }

    renderHistory() {
        return html`<user-history></user-history>`;
    }

    renderHeader() {
        const { userData } = this;
        const { display_name } = userData;
        if (this.isViewMode()) {
            return html`<h1>${display_name}</h1>`;
        }
        if (this.isEditMode()) {
            return html`<h1>Edit: ${display_name}</h1>`;
        }
        if (this.isAddNewMode()) {
            return html`<h1>Add new user</h1>`;
        }
    }
    isAddNewMode() {
        const { detailMode } = this;
        return detailMode === DETAIL_MODES.NEW;
    }

    renderControls() {
        // todo how much of this could be re-used?
        const { userData, isSaving, isLoading, activeAuthSite, authorizations, activeAuthSiteId } =
            this;
        const disableForAction = isSaving || isLoading;
        const eUserId = userData?.eUserId;

        const controls = [];
        if (this.isEditMode() || this.isAddNewMode()) {
            const saveText = this.isAddNewMode() ? 'Create New' : 'Save';
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    color="primary"
                    @click=${this.handleSave}
                    >${saveText}</tmn-button
                >`
            );
        }
        if (this.isViewMode()) {
            const routeData = { eUserId, authSite: activeAuthSite };
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    .data=${routeData}
                    @click=${this.editUser}
                    >Edit
                </tmn-button>`
            );
        }
        if (this.isEditMode()) {
            const routeData = { eUserId, authSite: activeAuthSite };
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    .data=${routeData}
                    @click=${this.viewUser}
                    >Cancel</tmn-button
                >`
            );
        }
        if (this.isAddNewMode()) {
            const routeData = { authSite: activeAuthSite };
            controls.push(
                html`<tmn-button
                    .disabled=${disableForAction}
                    .data=${routeData}
                    @click=${this.cancelNewUser}
                    >Cancel</tmn-button
                >`
            );
        }

        if (!this.isAddNewMode()) {
            if (userData.active) {
                controls.push(
                    html`<tmn-button .disabled=${disableForAction} @click=${this.handleDeactivate}
                        >Deactivate</tmn-button
                    >`
                );
            } else {
                controls.push(
                    html`<tmn-button .disabled=${disableForAction} @click=${this.handleActivate}
                        >Activate</tmn-button
                    >`
                );
            }
            const routeData = { authSite: activeAuthSite };
            controls.push(html`<routed-button
                name="user-new"
                .disabled=${disableForAction}
                .data=${routeData}>
                New User
            </routed-button>`);

            if (activeAuthSite && userData?.eUserId) {
                // this is a hack to make the "eventData" match the row data from the table
                // for the sake of these buttons
                const { eUserId, display_name } = userData;
                const eventData = {
                    metaData: {
                        id: eUserId,
                        rawData: {
                            display_name: display_name,
                        },
                    },
                };
                controls.push(
                    html`<act-as-btn
                            name="act-as"
                            .eventData=${eventData}
                            .eSiteId=${activeAuthSiteId}
                            .useIconButton=${false}
                            @tmn-act-as-confirmed=${this.actAs}>
                        </act-as-btn>
                        <reset-password-btn
                            name="reset-pw"
                            .useIconButton=${false}
                            .displayName=${display_name}
                            @tmn-reset-password-confirmed=${this.resetPassword}>
                        </reset-password-btn>`
                );
            }
            if (userData.active && authorizations.includes(CAN_EDIT_USER_TOKENS)) {
                controls.push(
                    html`<generate-token-btn
                        .disabled=${disableForAction}
                        .eUserId=${userData.eUserId}
                        .eventData=${userData}></generate-token-btn>`
                );
            }
        }
        return html`<div class="controls-container">${controls}</div> `;
    }

    renderViewDetails() {
        const { userTemplate, userData } = this;
        if (!userData?.username) {
            return null;
        }
        return html`${this.renderGroup(userTemplate)}`;
    }

    handleSave() {
        const { userData } = this;
        const data = { ...userData };

        if (this.isEditMode()) {
            this.actions.saveUser({ data });
            return;
        }
        this.actions.addUser({ data });
    }

    editUser() {
        const { userData } = this;
        this.actions.editUserDetails({ eUserId: userData.eUserId });
    }

    viewUser() {
        const { userData } = this;
        this.actions.viewUserDetails({ eUserId: userData.eUserId });
    }

    newUser() {
        this.actions.newUser();
    }

    cancelNewUser() {
        this.actions.cancelUserChanges();
    }

    handleActivate() {
        const { userData } = this;
        this.actions.activateUser({ eUserId: userData.eUserId });
    }

    handleDeactivate() {
        const { userData } = this;
        this.actions.deactivateUser({ eUserId: userData.eUserId });
    }

    resetPassword(e) {
        if (e) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
        }
        const { userData } = this;
        const params = { eUserId: userData.eUserId };
        this.actions.resetPassword(params);
    }

    actAs(e) {
        if (e) {
            e.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
        }
        const { detail } = e;
        const params = { eUserId: detail?.metaData?.id };
        this.actions.actAsUser(params);
    }
}
const mapStateToProps = (state, ownProps) => {
    return (state) => {
        return {
            activeAuthSite: selectors.activeAuthSiteNameSelector(state),
            activeAuthSiteId: selectors.activeAuthSiteIdSelector(state),
            userTemplate: selectors.userConfigTemplateSelector(state),
            validationErrors: selectors.userValidationErrorsSelector(state),
            userData: selectors.userStagedEditsSelector(state),
            detailMode: selectors.userDetailModeSelector(state),
            isSaving: selectors.userIsSavingSelector(state),
            isLoading: selectors.userIsLoadingSelector(state),
            authorizations: accountSelectors.accountAuthorizationsSelector(state),
        };
    };
};

const combinedActions = {
    ...actions,
};

customElements.define('user-detail', connect(mapStateToProps, combinedActions)(UserDetail));
